import { Maximize2, X } from "lucide-react";
import { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "~/components/ui";

import { EntityDetail } from "./EntityDetail";

interface EntityPeekProps {
  /** When set, the sheet is open and shows the detail of this entity id. */
  id: string | null;
  onClose: () => void;
}

/**
 * Slide-in side panel mirroring the category peek UX. Driven by the
 * `selectedEntity` URL search param so a refresh restores the panel. The
 * maximize icon opens the full-page detail route.
 */
export function EntityPeek({ id, onClose }: EntityPeekProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const open = id !== null;

  const openFullPage = () => {
    if (!id) return;
    onClose();
    navigate(`/entities/${id}`);
  };

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent side="right" showClose={false} className="flex flex-col p-0">
        <SheetHeader className="flex flex-row items-center justify-between gap-2">
          <SheetTitle className="capitalize">{t("model.record.entity.modelName")}</SheetTitle>
          <div className="flex items-center gap-1">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={openFullPage}
                  aria-label={t("ui.openInFullPage", { defaultValue: "Open full page" })}
                >
                  <Maximize2 />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.openInFullPage", { defaultValue: "Open full page" })}</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  aria-label={t("ui.close", { defaultValue: "Close" })}
                >
                  <X />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("ui.close", { defaultValue: "Close" })}</TooltipContent>
            </Tooltip>
          </div>
        </SheetHeader>
        <SheetBody>
          <Suspense fallback={<DetailLoading />}>
            {id !== null && <EntityDetail id={id} onBack={onClose} variant="peek" />}
          </Suspense>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
